import { useCallback, useEffect, useRef, useState } from 'react';
import {
  WORKFLOW_HISTORY_MAX,
  cloneWorkflowSnapshot,
  getUndoRedoShortcutLabels,
} from '../utils/workflowUndoRedoUtils';

/**
 * Tracks workflow edit history for undo/redo in the builder.
 */
export function useWorkflowUndoRedo({ getWorkflow, applyWorkflow, enabled = true }) {
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  const applyingRef = useRef(false);

  const recordChange = useCallback((label, previousWorkflow) => {
    if (!enabled || applyingRef.current || !previousWorkflow) return;
    setPast((prev) => [
      ...prev.slice(-(WORKFLOW_HISTORY_MAX - 1)),
      { label: label || 'Workflow change', snapshot: cloneWorkflowSnapshot(previousWorkflow) },
    ]);
    setFuture([]);
  }, [enabled]);

  const undo = useCallback(() => {
    if (!enabled || !past.length) return false;
    const entry = past[past.length - 1];
    const current = getWorkflow();
    applyingRef.current = true;
    setPast((prev) => prev.slice(0, -1));
    setFuture((prev) => [
      { label: 'Redo', snapshot: cloneWorkflowSnapshot(current) },
      ...prev,
    ]);
    applyWorkflow(entry.snapshot);
    applyingRef.current = false;
    return true;
  }, [enabled, past, getWorkflow, applyWorkflow]);

  const redo = useCallback(() => {
    if (!enabled || !future.length) return false;
    const entry = future[0];
    const current = getWorkflow();
    applyingRef.current = true;
    setFuture((prev) => prev.slice(1));
    setPast((prev) => [
      ...prev.slice(-(WORKFLOW_HISTORY_MAX - 1)),
      { label: 'Undo', snapshot: cloneWorkflowSnapshot(current) },
    ]);
    applyWorkflow(entry.snapshot);
    applyingRef.current = false;
    return true;
  }, [enabled, future, getWorkflow, applyWorkflow]);

  const clearHistory = useCallback(() => {
    setPast([]);
    setFuture([]);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    const shortcuts = getUndoRedoShortcutLabels();
    const handleKeyDown = (event) => {
      const key = event.key.toLowerCase();
      const mod = event.metaKey || event.ctrlKey;
      if (!mod) return;

      if (key === 'z' && !event.shiftKey) {
        event.preventDefault();
        undo();
        return;
      }

      if (key === 'y' || (key === 'z' && event.shiftKey)) {
        event.preventDefault();
        redo();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled, undo, redo]);

  return {
    past,
    future,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    recordChange,
    undo,
    redo,
    clearHistory,
    shortcutLabels: getUndoRedoShortcutLabels(),
  };
}

export default useWorkflowUndoRedo;
